/**
 * NIGHT SHIFT, the chill soundtrack (WebAudio, no assets; picked in the menu, COLD WAR is the
 * default). Lofi hip-hop for the bunker's night shift: Rhodes chords (two-operator FM) over a
 * swung boom-bap kit, a plucked bass, a kalimba motif in F major pentatonic, vinyl crackle, tape
 * hiss and rain, all through a tape wobble, a soft saturator and a dark low-pass. It follows the
 * same cue as the default score (`MusicCue`):
 *   stage    — lobby/aftermath: chords + rain, no drums; watch: drums through the wall (the filter
 *              opens with tension); weapons free: a needle drop, the filter opens; capitals
 *              exposed: the kalimba talks more; final: the loop turns minor (Dm9 Bbmaj9 Gm9 A7b9b13).
 *   inbound  — everything muffles and drops 3 dB, as if a door closed; the kalimba falls silent.
 *   hit      — at home: a tape stop, two seconds of nothing, a needle drop (at most every 25 s);
 *              elsewhere: the music dips 6 dB under the vinyl and rain.
 *   city     — each one lost takes a note out of the kalimba's scale; the Rhodes plays it goodbye.
 *   critical — snare, hats and kalimba leave; the kick plays a heartbeat on the grid that skips.
 *   eliminated — chords far away in the rain.
 * Four-bar sections rotate through forms (A A B A / A B A D / B A D A); every third 16-bar cycle
 * breaks down for two bars and odd cycles play drop-2 voicings, so a 20-minute match does not hear
 * one loop 23 times. Mix and arrangement tuned in the score lab with a critic over measured passes.
 */
import type { MusicCue } from './music';

const BPM = 74;
/** a 16th (s) */
const STEP = 60 / BPM / 4;
/** 16th swing: odd steps land this fraction of a step late (≈ 63 %) */
const SWING = 0.26;
/** the snare sits this far behind the grid (s): lazy */
const SNARE_LAG = 0.014;
const TICK_MS = 50;
/** scheduling lookahead (s); a hidden tab's timers run about once a second */
const AHEAD_S = 0.3;
const AHEAD_HIDDEN_S = 1.5;
/** output level: as loud as COLD WAR (K-weighted, within ~1 dB) */
const LEVEL = 1;
/** a tape stop at most this often (s); closer hits only dip */
const TAPE_STOP_EVERY_S = 25;
/** `MusicCue.stage` values this track reacts to by name */
const WATCH = 1;
const RELEASE = 2;
const FINAL = 4;
const AFTERMATH = 5;

/** level per stage (lobby, watch, weapons free, capitals exposed, final, aftermath) × base; send: reverb */
const LAYERS = {
  rhodes: { base: 0.6, send: 0.3, lv: [0.85, 1, 1, 1, 1, 0.8] },
  bass: { base: 0.15, send: 0.03, lv: [0.4, 0.9, 1, 1, 1, 0] },
  kick: { base: 0.5, send: 0.02, lv: [0, 0.8, 1, 1, 1, 0] },
  snare: { base: 0.42, send: 0.18, lv: [0, 0.7, 1, 1, 1, 0] },
  hats: { base: 0.2, send: 0.04, lv: [0, 0.6, 1, 1, 0.85, 0] },
  melody: { base: 0.38, send: 0.35, lv: [0, 0.5, 0.75, 1, 0.8, 0] },
  vinyl: { base: 0.16, send: 0, lv: [1, 0.9, 0.8, 0.8, 0.9, 1] },
  hiss: { base: 0.025, send: 0, lv: [1, 1, 1, 1, 1, 1] },
  rain: { base: 0.08, send: 0.05, lv: [1, 0.55, 0.4, 0.35, 0.5, 1] },
};
type LayerId = keyof typeof LAYERS;
/** the drum bus low-pass by stage (Hz): through the wall → in the room (watch: opens with tension) */
const DRUM_LP = [400, 1100, 8000, 9000, 7000, 400];
/** the whole mix's low-pass: lofi-dark; inbound and knocked out go darker */
const TONE_HZ = 5800;
const INBOUND_HZ = 900;
const ELIMINATED_HZ = 1400;

type Chord = readonly [root: number, voicing: readonly number[]];
/** chords: bass root + a rootless Rhodes voicing (MIDI), voice-led */
const PROG: Record<'A' | 'B' | 'C' | 'D', readonly Chord[]> = {
  A: [[43, [58, 62, 65, 69]], [36, [58, 62, 64, 69]], [41, [57, 60, 64, 67]], [38, [60, 64, 65, 69]]], // Gm9 C13 Fmaj9 Dm9
  B: [[46, [62, 65, 69, 72]], [45, [60, 64, 67, 71]], [43, [58, 62, 65, 69]], [36, [58, 62, 65, 67]]], // Bbmaj9 Am9 Gm9 C9sus4
  C: [[38, [60, 64, 65, 69]], [46, [62, 65, 69, 72]], [43, [58, 62, 65, 69]], [45, [55, 61, 65, 70]]], // Dm9 Bbmaj9 Gm9 A7b9b13
  D: [[46, [62, 65, 69, 72]], [46, [61, 65, 68, 72]], [45, [60, 64, 67, 71]], [38, [60, 63, 66, 69]]], // Bbmaj9 Bbm9 Am9 D7b9
};
type Form = keyof typeof PROG;
/** section forms, rotating every 32 bars */
const FORMS: readonly (readonly Form[])[] = [['A', 'A', 'B', 'A'], ['A', 'B', 'A', 'D'], ['B', 'A', 'D', 'A']];
/** the kalimba's notes (F major pentatonic, C4…D5): one per city we can lose */
const SCALE = [60, 62, 65, 67, 69, 72, 74];
/** which note each lost city takes, in order */
const LOST_ORDER = [5, 1, 3, 6, 0, 4, 2];
/** chance of a kalimba phrase every two bars, by stage */
const PHRASE = [0, 0.3, 0.45, 0.65, 0.5, 0];

const midi = (n: number) => 440 * 2 ** ((n - 69) / 12);
const rand = (a: number, b: number) => a + Math.random() * (b - a);
const pick = <T>(xs: readonly T[]): T => xs[Math.floor(Math.random() * xs.length)];

interface Layer {
  in: GainNode;
  fader: GainNode;
  last: number;
}

interface Graph {
  post: GainNode;
  /** the tape stop (everything) */
  duck: GainNode;
  /** far hits and inbound (the music, not the beds) */
  dip: GainNode;
  tone: BiquadFilterNode;
  /** the chords' and kalimba's bus, pumped by the kick */
  pump: GainNode;
  drumLp: BiquadFilterNode;
  /** wow + flutter + the tape-stop bend, into every pitched voice's detune */
  wobble: GainNode;
  bend: ConstantSourceNode;
  layers: Record<LayerId, Layer>;
  /** into the Rhodes pickup, tremolo and width */
  rhodesIn: GainNode;
  noise: AudioBuffer;
}

/** one bar's plan: comp hits [step, length, velocity, voicing?], bass [step, note, length], drums */
interface BarPlan {
  voicing: readonly number[];
  comp: [number, number, number, (readonly number[])?][];
  bass: [number, number, number][];
  kicks: Set<number>;
  ghost: number;
  openHat: number;
  /** critical: kick velocity by step */
  heart: Map<number, number>;
  /** a breakdown bar: one kick, no bass */
  brk: boolean;
}

export class NightShift {
  private cue: MusicCue;
  private lost: number;
  private readonly g: Graph;
  private readonly sources: AudioScheduledSourceNode[] = [];
  private readonly timer: ReturnType<typeof setInterval>;
  private disposed = false;
  private bar = 0;
  private stepIdx = 0;
  private nextStep: number;
  private plan: BarPlan | null = null;
  private form: Form = 'A';
  /** the last bar pushed this bar's chord onto its a-of-4 */
  private anticipated = false;
  /** the kalimba's two-bar motif ([step offset, scale index]), renewed every 16 bars */
  private motif: [number, number][] | null = null;
  /** kalimba notes by absolute step (bar * 16 + step) */
  private readonly melody = new Map<number, number>();
  private pauseUntil = 0;
  private lastStop = -Infinity;
  private lastFarHit = -Infinity;
  private drumLp = DRUM_LP[0];
  private drumTau = 1.2;
  private readonly next = { crackle: 0, pop: 0, drop: 0 };

  constructor(
    private readonly ctx: AudioContext,
    dest: AudioNode,
    cue: MusicCue,
    citiesLost: number,
  ) {
    this.cue = cue;
    this.lost = citiesLost;
    this.nextStep = ctx.currentTime + 0.1;
    this.g = this.build(dest);
    this.timer = setInterval(() => this.tick(), TICK_MS);
  }

  newMatch(): void {
    this.lost = 0;
  }

  /** A city lost: its kalimba note leaves the scale, and the Rhodes plays it once, on the next beat. */
  cityLost(): void {
    if (this.lost >= SCALE.length) return;
    const m = SCALE[LOST_ORDER[this.lost]];
    this.lost++;
    const t = this.ctx.currentTime;
    let at = Math.min(Math.max(t + 0.05, this.nextStep + ((4 - (this.stepIdx % 4)) % 4) * STEP), t + STEP * 4);
    if (t < this.pauseUntil) at = this.pauseUntil + 0.3; // after the needle drop, not into the tape stop
    this.rhodesNote(at, m, 0.45, 2.5);
  }

  set(cue: MusicCue): void {
    const prev = this.cue;
    this.cue = cue;
    const g = this.g;
    const t = this.ctx.currentTime;
    if (cue.stage !== prev.stage) {
      if (cue.stage === AFTERMATH) this.tapeStop(true); // the match is over
      if (cue.stage === RELEASE && prev.stage === WATCH) {
        this.needle(t + 0.05);
        this.drumTau = 2.5;
      }
    }
    if (cue.inbound !== prev.inbound || cue.eliminated !== prev.eliminated) ramp(g.tone.frequency, this.toneHz(), cue.inbound ? 0.4 : 1.5, t);
    if (cue.inbound !== prev.inbound && t >= this.pauseUntil) ramp(g.dip.gain, this.dipLevel(), cue.inbound ? 0.4 : 1.5, t);
    const crit = cue.critical && !cue.eliminated;
    // critical from mid-bar: the heartbeat starts in this bar
    if (crit && !(prev.critical && !prev.eliminated) && this.plan) this.plan.heart = heartbeat(cue.tension);
  }

  /** A detonation: at home a tape stop (at most every 25 s); elsewhere the music dips under the beds. */
  hit(near: boolean): void {
    const t = this.ctx.currentTime;
    if (t < this.pauseUntil) return; // the tape is stopped already
    if (near && t - this.lastStop >= TAPE_STOP_EVERY_S) {
      this.tapeStop(false);
      return;
    }
    if (t - this.lastFarHit < 3) return;
    this.lastFarHit = t;
    const d = this.g.dip.gain;
    d.cancelScheduledValues(t);
    d.setValueAtTime(d.value, t);
    d.setTargetAtTime(0.5, t + 0.3, 0.12);
    d.setTargetAtTime(this.dipLevel(), t + 1.3, 1);
    this.pop(t + 0.3, 0.8);
  }

  dispose(): void {
    this.disposed = true;
    clearInterval(this.timer);
    const t = this.ctx.currentTime;
    this.g.post.gain.cancelScheduledValues(t);
    this.g.post.gain.setTargetAtTime(0, t, 0.15);
    setTimeout(() => {
      for (const s of this.sources) s.stop();
      this.g.post.disconnect();
    }, 1200);
  }

  /** the music's level under the beds: 3 dB down while a warhead is coming at us */
  private dipLevel(): number {
    return this.cue.inbound && !this.cue.eliminated ? 0.7 : 1;
  }

  private toneHz(): number {
    return this.cue.eliminated ? ELIMINATED_HZ : this.cue.inbound ? INBOUND_HZ : TONE_HZ;
  }

  // ------------------------------------------------------------------ graph

  private build(dest: AudioNode): Graph {
    const ctx = this.ctx;
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -9;
    limiter.knee.value = 6;
    limiter.ratio.value = 6;
    limiter.attack.value = 0.004;
    limiter.release.value = 0.25;
    const post = gain(ctx, 0);
    post.gain.setTargetAtTime(LEVEL, ctx.currentTime, 1.5);
    post.connect(limiter).connect(dest);
    // music → high-pass → tone low-pass → tape saturation → dip → duck → post; the beds join at the duck
    const duck = gain(ctx, 1);
    duck.connect(post);
    const dip = gain(ctx, 1);
    dip.connect(duck);
    const sat = saturator(ctx, 1.6);
    sat.connect(dip);
    const tone = filt(ctx, 'lowpass', TONE_HZ, 0.6);
    tone.connect(sat);
    const hp = filt(ctx, 'highpass', 40, 0.7);
    hp.connect(tone);
    const verb = ctx.createConvolver();
    verb.buffer = room(ctx, 2.2);
    const reverb = gain(ctx, 1);
    reverb.connect(verb).connect(gain(ctx, 0.8)).connect(hp);
    const pump = gain(ctx, 1);
    pump.connect(hp);
    // drums: glued (bus compressor + saturation) like a sampled break, then through the wall or not
    const drums = gain(ctx, 1);
    const glue = ctx.createDynamicsCompressor();
    glue.threshold.value = -20;
    glue.ratio.value = 4;
    glue.attack.value = 0.01;
    glue.release.value = 0.12;
    glue.knee.value = 6;
    const drumLp = filt(ctx, 'lowpass', DRUM_LP[0], 0.5);
    drums.connect(glue).connect(saturator(ctx, 2.5)).connect(drumLp).connect(hp);
    // the kalimba's dotted-8th echo
    const echo = ctx.createDelay(2);
    echo.delayTime.value = STEP * 3;
    const echoLp = filt(ctx, 'lowpass', 1600);
    echo.connect(echoLp).connect(gain(ctx, 0.32)).connect(echo);
    echoLp.connect(gain(ctx, 0.5)).connect(pump);
    // tape wobble: two slow wows + flutter + a bend for tape stops
    const wobble = gain(ctx, 1);
    const bend = ctx.createConstantSource();
    bend.offset.value = 0;
    bend.connect(wobble);
    this.start(bend);
    for (const [rate, depth] of [[0.31, 6], [0.53, 4], [5.6, 2.2]]) this.start(osc(ctx, 'sine', rate)).connect(gain(ctx, depth)).connect(wobble);

    const buses: Record<LayerId, AudioNode> = { rhodes: pump, bass: hp, melody: pump, kick: drums, snare: drums, hats: drums, vinyl: duck, hiss: duck, rain: duck };
    const layers = {} as Record<LayerId, Layer>;
    for (const id of Object.keys(LAYERS) as LayerId[]) {
      const l: Layer = { in: gain(ctx, 1), fader: gain(ctx, 0), last: -1 };
      l.in.connect(l.fader).connect(buses[id]);
      if (LAYERS[id].send) l.fader.connect(gain(ctx, LAYERS[id].send)).connect(reverb);
      layers[id] = l;
    }
    layers.melody.fader.connect(gain(ctx, 0.6)).connect(echo);
    // Rhodes: an asymmetric pickup (even harmonics), a gentle auto-pan in 8ths, and a slowly
    // drifting 14 ms double on the right for width
    const rhodesIn = gain(ctx, 1);
    const pickup = ctx.createWaveShaper();
    const pc = new Float32Array(1024);
    for (let i = 0; i < pc.length; i++) {
      const x = (i / (pc.length - 1)) * 2 - 1;
      pc[i] = x + 0.3 * x * x;
    }
    pickup.curve = pc;
    rhodesIn.connect(pickup);
    const pan = ctx.createStereoPanner();
    this.start(osc(ctx, 'sine', (BPM / 60) * 2)).connect(gain(ctx, 0.2)).connect(pan.pan);
    pickup.connect(pan).connect(layers.rhodes.in);
    const wide = ctx.createDelay(0.05);
    wide.delayTime.value = 0.014;
    this.start(osc(ctx, 'sine', 0.27)).connect(gain(ctx, 0.0015)).connect(wide.delayTime);
    const merger = ctx.createChannelMerger(2);
    pickup.connect(wide).connect(merger, 0, 1);
    merger.connect(gain(ctx, 0.35)).connect(layers.rhodes.in);

    // beds, each on its own noise loop (no audible 3 s repeat): tape hiss, vinyl surface, rain
    const loop = (seconds: number) => {
      const s = ctx.createBufferSource();
      s.buffer = noise(ctx, seconds);
      s.loop = true;
      return this.start(s);
    };
    loop(7.3).connect(filt(ctx, 'highpass', 6000)).connect(gain(ctx, 0.25)).connect(layers.hiss.in);
    loop(9.1).connect(filt(ctx, 'bandpass', 900, 0.5)).connect(gain(ctx, 0.08)).connect(layers.vinyl.in);
    const rainAmp = gain(ctx, 0.5);
    this.start(osc(ctx, 'sine', 0.05)).connect(gain(ctx, 0.15)).connect(rainAmp.gain);
    loop(11.7).connect(filt(ctx, 'highpass', 500)).connect(filt(ctx, 'lowpass', 5500)).connect(rainAmp).connect(layers.rain.in);

    return { post, duck, dip, tone, pump, drumLp, wobble, bend, layers, rhodesIn, noise: noise(ctx, 3) };
  }

  /** A persistent source: started now, stopped on dispose. */
  private start<T extends AudioScheduledSourceNode>(s: T): T {
    s.start();
    this.sources.push(s);
    return s;
  }

  /** Every pitched voice rides the tape wobble; the link goes when the voice ends. */
  private wobbled(o: OscillatorNode): OscillatorNode {
    const w = this.g.wobble;
    w.connect(o.detune);
    o.onended = () => {
      try {
        w.disconnect(o.detune);
      } catch {
        /* gone already */
      }
    };
    return o;
  }

  // ------------------------------------------------------------------ control loop

  private tick(): void {
    const g = this.g;
    const c = this.cue;
    const t = this.ctx.currentTime;
    const ahead = t + (document.hidden ? AHEAD_HIDDEN_S : AHEAD_S);
    const crit = c.critical && !c.eliminated;
    const tension = Math.max(0, Math.min(1, c.tension));
    for (const id of Object.keys(LAYERS) as LayerId[]) {
      let lvl = LAYERS[id].lv[c.stage];
      if (crit && (id === 'snare' || id === 'hats' || id === 'melody')) lvl = 0;
      if (crit && id === 'kick') lvl = 1;
      if (c.inbound && id === 'melody') lvl = 0; // no pings under the alarm
      if (c.eliminated) lvl = id === 'rhodes' ? 0.7 : id === 'rain' || id === 'vinyl' || id === 'hiss' ? 1 : 0;
      const target = LAYERS[id].base * lvl;
      const l = g.layers[id];
      if (Math.abs(target - l.last) > 1e-4) {
        ramp(l.fader.gain, target, 2, t);
        l.last = target;
      }
    }
    const lp = c.stage === WATCH ? DRUM_LP[WATCH] + 1800 * tension : DRUM_LP[c.stage];
    if (Math.abs(lp - this.drumLp) > 100) {
      ramp(g.drumLp.frequency, lp, this.drumTau, t);
      this.drumLp = lp;
      this.drumTau = 1.2;
    }
    // vinyl crackle (Poisson) and the odd pop; rain droplets
    if (this.next.crackle < t) this.next.crackle = t;
    while (this.next.crackle <= ahead) {
      this.crackle(this.next.crackle);
      this.next.crackle += -Math.log(1 - Math.random()) / 7;
    }
    if (this.next.pop < t) this.next.pop = t + rand(1, 4);
    if (this.next.pop <= ahead) {
      this.pop(this.next.pop, rand(0.2, 0.6));
      this.next.pop += rand(2, 7);
    }
    if (this.next.drop < t) this.next.drop = t;
    while (this.next.drop <= ahead) {
      this.droplet(this.next.drop);
      this.next.drop += -Math.log(1 - Math.random()) / 9;
    }

    if (t < this.pauseUntil) return; // the tape is stopped
    // late (a hidden tab woke up): skip the missed steps, keep the bar phase
    while (this.nextStep < t) this.advance();
    while (this.nextStep <= ahead) {
      this.step(this.stepIdx, this.nextStep, crit, tension);
      this.advance();
    }
  }

  private advance(): void {
    this.nextStep += STEP;
    this.stepIdx = (this.stepIdx + 1) % 16;
    if (this.stepIdx === 0) this.bar++;
  }

  /** The form of bar `b`: a section's form is fixed at its first bar (the final's minor loop too). */
  private formAt(b: number): Form {
    if (b % 4 !== 0) return this.form;
    return this.cue.stage === FINAL ? 'C' : FORMS[Math.floor(b / 32) % 3][Math.floor(b / 4) % 4];
  }

  /** One 16th of the groove. */
  private step(s: number, t: number, crit: boolean, tension: number): void {
    const c = this.cue;
    // a new bar draws its plan (also mid-bar, when catching up after a stall left none)
    if (s === 0 || !this.plan) this.plan = this.planBar(tension, crit);
    const p = this.plan;
    const at = t + (s % 2 ? SWING * STEP : 0);
    const human = () => rand(-0.006, 0.006);
    for (const [st, len, vel, v] of p.comp) if (st === s) this.chord(at, v ?? p.voicing, vel, len * STEP);
    if (!c.eliminated && !p.brk) for (const [st, note, len] of p.bass) if (st === s) this.bassNote(at + human(), note, len * STEP);
    const drumsOn = c.stage >= WATCH && c.stage <= FINAL && !c.eliminated;
    if (drumsOn && crit) {
      // a heartbeat on the grid that sometimes skips
      const v = p.heart.get(s);
      if (v) this.kick(at, v);
    } else if (drumsOn && p.brk) {
      if (s === 0) this.kick(at, 0.8);
    } else if (drumsOn) {
      if (p.kicks.has(s)) this.kick(at + human(), s === 0 ? 1 : 0.85);
      if (s === 4 || s === 12) this.snare(at + SNARE_LAG + human(), 1);
      else if (p.ghost === s) this.snare(at + SNARE_LAG + human(), 0.3);
      if (s % 2 === 0) this.hat(at + human(), (s % 4 === 0 ? 0.9 : 0.6) * rand(0.85, 1.1), p.openHat === s);
      else if (c.stage !== FINAL && Math.random() < 0.35 + 0.2 * tension) this.hat(at + human(), 0.3 * rand(0.8, 1.2), false);
    }
    const key = this.bar * 16 + s;
    const n = this.melody.get(key);
    if (n !== undefined) {
      this.melody.delete(key);
      const gone = new Set(LOST_ORDER.slice(0, this.lost));
      if (!crit && !c.eliminated && !c.inbound && !gone.has(SCALE.indexOf(n))) this.kalimba(at + human(), n, rand(0.55, 0.8));
    }
  }

  private planBar(tension: number, crit: boolean): BarPlan {
    const c = this.cue;
    this.form = this.formAt(this.bar);
    const [root] = PROG[this.form][this.bar % 4];
    const voicing = this.voicingAt(this.bar, this.form);
    // comping: whole bar / push on the and-of-3 / short stabs
    const r = Math.random();
    const sparse = c.stage === 0 || c.stage === AFTERMATH || c.eliminated;
    let comp: BarPlan['comp'] = sparse || r < 0.35 ? [[0, 16, 0.7]] : r < 0.85 ? [[0, 10, 0.72], [10, 6, 0.5]] : [[0, 4, 0.7], [6, 3, 0.5], [10, 6, 0.55]];
    // the last bar pushed this one's chord early: no downbeat chord
    if (this.anticipated) comp = comp.filter(([st]) => st !== 0);
    this.anticipated = false;
    // a fifth of the time the next chord comes in on the a-of-4 (the most human thing keys do)
    if (!sparse && !breakdown(this.bar + 1) && Math.random() < 0.2) {
      const nextVoicing = this.voicingAt(this.bar + 1, this.formAt(this.bar + 1));
      comp = comp.filter(([st]) => st < 14).map(([st, len, vel]): BarPlan['comp'][number] => [st, Math.min(len, 14 - st), vel]);
      comp.push([14, 18, 0.6, nextVoicing]);
      this.anticipated = true;
    }
    // bass: root, a pickup, sometimes a chromatic walk into the next bar's root
    const nextRoot = PROG[this.formAt(this.bar + 1)][(this.bar + 1) % 4][0];
    const bass: BarPlan['bass'] = [[0, root, 6]];
    bass.push(Math.random() < 0.5 ? [7, root, 3] : [10, Math.random() < 0.3 ? root + 7 : root, 4]);
    if (Math.random() < 0.3 && nextRoot !== root && !breakdown(this.bar + 1)) bass.push([14, nextRoot + (nextRoot > root ? -1 : 1), 2]);
    // kick: 1 and the and-of-3, sometimes the a-of-2 or the e-of-1
    const kicks = new Set([0, 10]);
    if (Math.random() < 0.3) kicks.add(7);
    if (Math.random() < 0.12) kicks.add(3);
    if (this.bar % 4 === 3 && Math.random() < 0.5) kicks.delete(10); // a breath before the turn
    const ghost = Math.random() < 0.2 ? 15 : Math.random() < 0.1 ? 6 : -1;
    const openHat = this.bar % 2 === 1 && Math.random() < 0.3 ? 14 : -1;
    // kalimba: a remembered two-bar motif, sometimes with one note moved
    if (this.bar % 16 === 0 || !this.motif) this.motif = newMotif();
    if (this.bar % 2 === 0 && !c.eliminated && Math.random() < PHRASE[c.stage] * (0.7 + 0.6 * tension)) this.phrase(this.motif);
    return { voicing, comp, bass, kicks, ghost, openHat, heart: crit ? heartbeat(tension) : new Map(), brk: breakdown(this.bar) };
  }

  /** The Rhodes voicing of bar `b`: drop-2 (the second voice from the top an octave down) on odd 16-bar cycles. */
  private voicingAt(b: number, form: Form): readonly number[] {
    const v = PROG[form][b % 4][1];
    return Math.floor(b / 16) % 2 ? [v[2] - 12, v[0], v[1], v[3]].sort((x, y) => x - y) : v;
  }

  private phrase(motif: [number, number][]): void {
    const moved = Math.random() < 0.3 ? Math.floor(Math.random() * motif.length) : -1;
    motif.forEach(([off, i], k) => {
      const j = k === moved ? Math.max(0, Math.min(SCALE.length - 1, i + pick([-1, 1]))) : i;
      this.melody.set(this.bar * 16 + off, SCALE[j]);
    });
  }

  // ------------------------------------------------------------------ voices

  /** A Rhodes chord, strummed up or down. */
  private chord(t: number, voicing: readonly number[], vel: number, dur: number): void {
    const spread = rand(0.008, 0.022);
    const order = Math.random() < 0.5 ? voicing : [...voicing].reverse();
    order.forEach((m, i) => this.rhodesNote(t + i * spread, m, vel * rand(0.85, 1.05), dur));
  }

  /** Two-operator FM electric piano: a bright attack that mellows and keeps fading, with a tine click. */
  private rhodesNote(t: number, m: number, vel: number, dur: number): void {
    const ctx = this.ctx;
    const f = midi(m);
    const car = osc(ctx, 'sine', f);
    const mod = osc(ctx, 'sine', f);
    const idx = gain(ctx, 0);
    idx.gain.setValueAtTime(f * 2.4 * vel, t);
    idx.gain.exponentialRampToValueAtTime(f * 0.8 * vel + 1, t + 0.35);
    idx.gain.exponentialRampToValueAtTime(f * 0.35 * vel + 1, t + dur + 0.5);
    mod.connect(idx).connect(car.frequency);
    const amp = gain(ctx, 0.0001);
    const end = t + dur;
    amp.gain.setValueAtTime(0.0001, t);
    amp.gain.exponentialRampToValueAtTime(0.25 * vel, t + 0.006);
    amp.gain.setTargetAtTime(0.04 * vel, t + 0.03, 1.1);
    amp.gain.setTargetAtTime(0.0001, end, 0.15);
    car.connect(amp).connect(this.g.rhodesIn);
    const tine = osc(ctx, 'sine', f * 7.1);
    const tg = gain(ctx, 0.0001);
    tg.gain.setValueAtTime(0.0001, t);
    tg.gain.exponentialRampToValueAtTime(0.02 * vel, t + 0.002);
    tg.gain.exponentialRampToValueAtTime(0.0001, t + 0.07);
    tine.connect(tg).connect(this.g.rhodesIn);
    for (const o of [car, mod, tine]) {
      this.wobbled(o);
      o.start(t);
      o.stop(o === tine ? t + 0.1 : end + 1.2);
    }
  }

  /** A plucked bass: sine, triangle and a quiet octave (so laptop speakers hear it), low-passed. */
  private bassNote(t: number, m: number, dur: number): void {
    const ctx = this.ctx;
    const f = midi(m);
    const lp = filt(ctx, 'lowpass', 800, 0.7);
    const amp = gain(ctx, 0.0001);
    amp.gain.setValueAtTime(0.0001, t);
    amp.gain.exponentialRampToValueAtTime(0.9, t + 0.012);
    amp.gain.setTargetAtTime(0.35, t + 0.03, 0.5);
    amp.gain.setTargetAtTime(0.0001, t + dur, 0.05);
    lp.connect(amp).connect(this.g.layers.bass.in);
    for (const [type, v, mult] of [['sine', 1, 1], ['triangle', 0.5, 1], ['sine', 0.18, 2]] as const) {
      const o = osc(ctx, type, f * mult);
      o.connect(gain(ctx, v)).connect(lp);
      this.wobbled(o);
      o.start(t);
      o.stop(t + dur + 0.4);
    }
  }

  /** A kalimba tine: the thumb's click, a pitch that settles, a few partials. */
  private kalimba(t: number, m: number, vel: number): void {
    const ctx = this.ctx;
    const f = midi(m);
    this.burst(t, 0.006, 0.06 * vel, 'bandpass', f * 3, this.g.layers.melody.in, 2);
    for (const [r, a, d] of [[1, 1, 1.4], [2, 0.12, 0.4], [5.95, 0.1, 0.12]]) {
      const o = osc(ctx, 'sine', f * r);
      const env = gain(ctx, 0.0001);
      o.detune.setValueAtTime(12, t);
      o.detune.linearRampToValueAtTime(0, t + 0.03);
      env.gain.setValueAtTime(0.0001, t);
      env.gain.exponentialRampToValueAtTime(a * 0.5 * vel, t + 0.003);
      env.gain.exponentialRampToValueAtTime(0.0001, t + d);
      o.connect(env).connect(this.g.layers.melody.in);
      this.wobbled(o);
      o.start(t);
      o.stop(t + d + 0.05);
    }
  }

  /** A soft kick with a knock; the chords and kalimba duck under it (the pump). */
  private kick(t: number, vel: number): void {
    const ctx = this.ctx;
    const into = this.g.layers.kick.in;
    const o = osc(ctx, 'sine', 110);
    const env = gain(ctx, 0.0001);
    o.frequency.setValueAtTime(110, t);
    o.frequency.exponentialRampToValueAtTime(50, t + 0.09);
    env.gain.setValueAtTime(0.0001, t);
    env.gain.exponentialRampToValueAtTime(vel, t + 0.004);
    env.gain.exponentialRampToValueAtTime(0.0001, t + 0.42);
    o.connect(env).connect(into);
    o.start(t);
    o.stop(t + 0.45);
    this.burst(t, 0.006, 0.25 * vel, 'lowpass', 3000, into);
    const knock = osc(ctx, 'sine', 180);
    const kg = gain(ctx, 0.0001);
    kg.gain.setValueAtTime(0.0001, t);
    kg.gain.exponentialRampToValueAtTime(0.3 * vel, t + 0.003);
    kg.gain.exponentialRampToValueAtTime(0.0001, t + 0.04);
    knock.connect(kg).connect(into);
    knock.start(t);
    knock.stop(t + 0.05);
    const p = this.g.pump.gain;
    p.setTargetAtTime(0.72, t, 0.008);
    p.setTargetAtTime(1, t + 0.07, 0.12);
  }

  private snare(t: number, vel: number): void {
    const ctx = this.ctx;
    this.burst(t, 0.18, 0.7 * vel, 'bandpass', 1600, this.g.layers.snare.in, 0.5);
    const body = osc(ctx, 'sine', 230);
    const env = gain(ctx, 0.0001);
    body.frequency.setValueAtTime(230, t);
    body.frequency.exponentialRampToValueAtTime(180, t + 0.025);
    env.gain.setValueAtTime(0.0001, t);
    env.gain.exponentialRampToValueAtTime(0.45 * vel, t + 0.003);
    env.gain.exponentialRampToValueAtTime(0.0001, t + 0.09);
    body.connect(env).connect(this.g.layers.snare.in);
    body.start(t);
    body.stop(t + 0.1);
  }

  private hat(t: number, vel: number, open: boolean): void {
    this.burst(t, open ? 0.26 : 0.035 + Math.random() * 0.015, 0.45 * vel, 'bandpass', 5200, this.g.layers.hats.in, 0.9);
  }

  private burst(t: number, dur: number, vol: number, type: BiquadFilterType, f: number, dest: AudioNode, q = 0.7): void {
    const ctx = this.ctx;
    const s = ctx.createBufferSource();
    s.buffer = this.g.noise;
    const env = gain(ctx, 0.0001);
    env.gain.setValueAtTime(0.0001, t);
    env.gain.exponentialRampToValueAtTime(vol, t + Math.min(0.003, dur / 4));
    env.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    s.connect(filt(ctx, type, f, q)).connect(env).connect(dest);
    s.start(t, Math.random() * 2, dur + 0.02);
  }

  private crackle(t: number): void {
    this.burst(t, 0.0008 + Math.random() * 0.0015, rand(0.1, 0.6), 'bandpass', rand(2000, 5000), this.g.layers.vinyl.in, 1.2);
  }

  private pop(t: number, vol: number): void {
    this.burst(t, 0.012, vol, 'lowpass', 1400, this.g.layers.vinyl.in);
  }

  private droplet(t: number): void {
    this.burst(t, rand(0.004, 0.012), rand(0.05, 0.3), 'bandpass', rand(1800, 6000), this.g.layers.rain.in, 2);
  }

  /** the needle drops: a soft pop and a short surface swell */
  private needle(t: number): void {
    this.pop(t, 0.9);
    for (let i = 0; i < 6; i++) this.crackle(t + 0.02 + i * 0.03);
  }

  /**
   * A tape stop: everything pitched sinks and slows to nothing, two seconds of silence, then a
   * needle drop on a fresh section. `final`: the match is over (never skipped).
   */
  private tapeStop(final: boolean): void {
    const g = this.g;
    const t = this.ctx.currentTime;
    if (t < this.pauseUntil && !final) return;
    this.lastStop = t;
    g.bend.offset.cancelScheduledValues(t);
    g.bend.offset.setValueAtTime(0, t);
    g.bend.offset.linearRampToValueAtTime(-2400, t + 0.8);
    g.duck.gain.cancelScheduledValues(t);
    g.duck.gain.setValueAtTime(g.duck.gain.value, t);
    g.duck.gain.linearRampToValueAtTime(0, t + 0.8);
    const back = t + 2.6;
    g.bend.offset.setValueAtTime(0, back);
    g.duck.gain.setValueAtTime(0, back);
    g.duck.gain.linearRampToValueAtTime(1, back + 0.25);
    this.pauseUntil = back;
    this.nextStep = back + 0.05;
    this.stepIdx = 0;
    this.bar = Math.ceil(this.bar / 4) * 4; // the start of a section
    this.melody.clear();
    setTimeout(() => this.disposed || this.needle(this.ctx.currentTime + 0.02), (back - t) * 1000);
  }
}

/** two bars out of every third 16-bar cycle: drums down to one kick, no bass */
function breakdown(b: number): boolean {
  return Math.floor(b / 16) % 3 === 2 && (b % 16 === 12 || b % 16 === 13);
}

/** critical: lub-dub pairs on 1 and 3 (and 2 and 4 when tense), one pair in eight skipped */
function heartbeat(tension: number): Map<number, number> {
  const heart = new Map<number, number>();
  for (const b of tension > 0.6 ? [0, 4, 8, 12] : [0, 8]) {
    if (Math.random() > 0.12) {
      heart.set(b, 0.8);
      heart.set(b + 3, 0.45);
    }
  }
  return heart;
}

function newMotif(): [number, number][] {
  const notes: [number, number][] = [];
  let at = pick([2, 4, 6, 8]);
  let i = Math.floor(rand(1, SCALE.length - 1));
  const count = Math.floor(rand(3, 6));
  for (let k = 0; k < count && at < 30; k++) {
    notes.push([at, i]);
    at += pick([2, 2, 3, 4]);
    i = Math.max(0, Math.min(SCALE.length - 1, i + pick([-2, -1, -1, 1, 1, 2])));
  }
  return notes;
}

// ------------------------------------------------------------------ node helpers

function osc(ctx: AudioContext, type: OscillatorType, f: number): OscillatorNode {
  const o = ctx.createOscillator();
  o.type = type;
  o.frequency.value = f;
  return o;
}

function gain(ctx: AudioContext, v: number): GainNode {
  const g = ctx.createGain();
  g.gain.value = v;
  return g;
}

function filt(ctx: AudioContext, type: BiquadFilterType, f: number, q = 0.7): BiquadFilterNode {
  const b = ctx.createBiquadFilter();
  b.type = type;
  b.frequency.value = f;
  b.Q.value = q;
  return b;
}

function ramp(param: AudioParam, target: number, tau: number, at: number): void {
  param.cancelScheduledValues(at);
  param.setValueAtTime(param.value, at);
  param.setTargetAtTime(target, at, tau);
}

function noise(ctx: AudioContext, seconds: number): AudioBuffer {
  const b = ctx.createBuffer(1, Math.floor(ctx.sampleRate * seconds), ctx.sampleRate);
  const d = b.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  return b;
}

/** a small warm room */
function room(ctx: AudioContext, seconds: number): AudioBuffer {
  const len = Math.floor(ctx.sampleRate * seconds);
  const b = ctx.createBuffer(2, len, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const d = b.getChannelData(ch);
    let y = 0;
    for (let i = 0; i < len; i++) {
      const k = i / len;
      y += (0.7 - 0.6 * k) * (Math.random() * 2 - 1 - y);
      d[i] = i < ctx.sampleRate * 0.012 ? 0 : y * (1 - k) ** 4;
    }
  }
  return b;
}

/** soft tape saturation */
function saturator(ctx: AudioContext, drive: number): WaveShaperNode {
  const ws = ctx.createWaveShaper();
  const curve = new Float32Array(2048);
  for (let i = 0; i < curve.length; i++) {
    const x = (i / (curve.length - 1)) * 2 - 1;
    curve[i] = Math.tanh(drive * x) / Math.tanh(drive);
  }
  ws.curve = curve;
  ws.oversample = '2x';
  return ws;
}
